import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { formatMoney, currencySymbol } from "../../lib/money";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Coins, TrendingUp, Gift, Users, Layers, Receipt, AlertTriangle,
  ShieldCheck, Sparkles, ToggleLeft, ToggleRight, Plus, Search, Edit2,
} from "lucide-react";

/**
 * Diplomexa Diploks — Admin Console (super_admin only).
 *
 * Five tabs, one page, no over-engineering:
 *   1. Revenue Dashboard  — KPIs, by-currency, by-entity, by-pack, top features.
 *   2. Bonus Campaigns    — Founding Organiser + new campaign creation.
 *   3. Pack Manager       — Edit pack prices / bonuses / active flag.
 *   4. Organisation Wallets — Browse + low-balance filter, manual adjust.
 *   5. Ledger             — Cross-org audit-clean ledger.
 *
 * Strict compliance wording is rendered at the bottom of every tab so every
 * admin screen restates the "Diploks are platform credits" notice.
 */
const TABS = [
  { key: "revenue",   label: "Revenue",       icon: TrendingUp },
  { key: "campaigns", label: "Campaigns",     icon: Gift },
  { key: "packs",     label: "Packs",         icon: Layers },
  { key: "orgs",      label: "Organisations", icon: Users },
  { key: "ledger",    label: "Ledger",        icon: Receipt },
];

const COMPLIANCE_NOTICE = (
  "Diploks are Diplomexa platform credits used to unlock eligible digital " +
  "services. Diploks are not cash, not cryptocurrency, not e-money, not a " +
  "stored-value wallet, not an investment product, not transferable, not " +
  "withdrawable, not resellable, and cannot be converted into money."
);

export default function DiploksAdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("revenue");

  // Client-side gate: server enforces super_admin; this just prevents the
  // 4-panel useEffect storm of 403 toasts when a non-super-admin lands here.
  if (user && user.role !== "super_admin") {
    return (
      <DashboardLayout title="Diploks Admin">
        <div
          data-testid="diploks-admin-forbidden"
          className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center max-w-xl mx-auto"
        >
          <ShieldCheck className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <p className="text-lg font-bold text-amber-900">Super-admin only</p>
          <p className="text-sm text-amber-800 mt-2">
            The Diploks Admin console manages platform-credit packs, bonus
            campaigns, the ledger and global revenue. Access is restricted to
            super-admin accounts. Please use the Diploks Wallet for your
            organisation instead.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Diploks Admin">
      <div className="space-y-6" data-testid="diploks-admin-page">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0A192F] via-[#0F2C50] to-[#0F4C81] text-white p-6 border border-[#D4AF37]/30">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <Coins className="h-3 w-3" /> Diplomexa Diploks · Admin
          </p>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold">Platform credits console</h1>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Manage packs, campaigns, organisation wallets and the global Diploks
            ledger. Every change is audit-logged.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="diploks-admin-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`diploks-admin-tab-${key}`}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 transition-colors ${
                tab === key
                  ? "border-[#D4AF37] text-[#0A192F]"
                  : "border-transparent text-slate-500 hover:text-[#0A192F]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* Active panel */}
        {tab === "revenue"   && <RevenuePanel />}
        {tab === "campaigns" && <CampaignsPanel />}
        {tab === "packs"     && <PacksPanel />}
        {tab === "orgs"      && <OrgsPanel />}
        {tab === "ledger"    && <LedgerPanel />}

        {/* Compliance footer */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11px] text-slate-600 leading-relaxed">
          <p className="font-bold text-slate-800 flex items-center gap-1.5 mb-1">
            <ShieldCheck className="h-3.5 w-3.5" /> About Diploks (compliance)
          </p>
          {COMPLIANCE_NOTICE}
        </div>
      </div>
    </DashboardLayout>
  );
}


// ============================================================================
// Revenue tab
// ============================================================================
function RevenuePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/diploks/revenue-dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-slate-500">Loading revenue insights…</p>;
  if (!data) return null;
  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="diploks-revenue-dashboard">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Paying organisations" value={t.paying_orgs} sub={`${t.total_orgs} wallets total`} />
        <Kpi label="Trial → paid" value={`${t.trial_to_paid_pct}%`} sub="conversion" />
        <Kpi label="Total top-ups" value={t.topup_count} sub={`${t.topup_failed} failed · ${t.topup_pending} pending`} />
        <Kpi label="Bonus grants" value={t.bonus_grants} sub="campaign + per-pack" accent="emerald" />
        <Kpi label="Expiring (30d)" value={t.expiring_soon_total} sub="Diploks" accent="amber" />
        <Kpi label="Low-balance orgs" value={t.low_balance_orgs} sub="< 20 Diploks left" accent="amber" />
      </div>

      {/* By currency + entity */}
      <div className="grid md:grid-cols-2 gap-4">
        <RevenueTable
          title="Revenue by currency"
          rows={data.revenue_by_currency || []}
          columns={["currency", "amount", "count", "diploks"]}
          renderAmount={(r) => formatMoney(r.amount, r.currency)}
        />
        <RevenueTable
          title="Revenue by billing entity"
          rows={data.revenue_by_entity || []}
          columns={["entity", "amount", "count", "diploks"]}
          renderEntity={(r) => `${r.entity} (${(r.currencies || []).join(", ")})`}
        />
      </div>

      {/* By pack */}
      <RevenueTable
        title="Revenue by pack"
        rows={data.revenue_by_pack || []}
        columns={["tier_key", "amount", "count"]}
        renderAmount={(r) => r.amount?.toLocaleString()}
      />

      {/* Top features + top consumers */}
      <div className="grid md:grid-cols-2 gap-4">
        <RevenueTable
          title="Most-used Diploks features"
          rows={data.top_features || []}
          columns={["feature_key", "total"]}
        />
        <RevenueTable
          title="Top consuming organisations"
          rows={data.top_consuming_orgs || []}
          columns={["organisation_id", "total"]}
        />
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  const accentCls = accent === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                   : accent === "amber"  ? "border-amber-200 bg-amber-50 text-amber-900"
                                          : "border-slate-200 bg-white text-[#0A192F]";
  return (
    <div className={`rounded-xl border p-4 ${accentCls}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-75">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value ?? "—"}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

function RevenueTable({ title, rows, columns, renderAmount, renderEntity }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <p className="text-sm font-bold text-[#0A192F]">{title}</p>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-slate-500">No data yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              {columns.map((c) => (
                <th key={c} className="px-4 py-2 text-left">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c} className="px-4 py-2 font-medium text-[#0A192F]">
                    {c === "amount" && renderAmount ? renderAmount(r)
                      : c === "entity" && renderEntity ? renderEntity(r)
                      : (r[c] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ============================================================================
// Campaigns tab
// ============================================================================
function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/admin/diploks/campaigns")
      .then((r) => setCampaigns(r.data.campaigns || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/diploks/campaigns/${c.id}`, { is_active: !c.is_active });
      toast.success(`${c.label} ${c.is_active ? "deactivated" : "activated"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="diploks-admin-campaigns">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Bonus campaigns reward purchasers with extra Diploks. Eligibility (e.g. first N orgs, first top-up only) enforces single-grant per organisation.
        </p>
      </div>
      {loading && <p className="text-sm text-slate-500">Loading campaigns…</p>}
      {!loading && campaigns.length === 0 && (
        <p className="text-sm text-slate-500">No campaigns configured.</p>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {campaigns.map((c) => (
          <div
            key={c.id}
            data-testid={`diploks-campaign-${c.key}`}
            className={`rounded-xl border p-4 ${
              c.is_active ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-slate-200 bg-white opacity-70"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37]">{c.key}</p>
                <p className="text-base font-bold text-[#0A192F]">{c.label}</p>
                {c.tagline && <p className="text-xs text-slate-500 mt-0.5">{c.tagline}</p>}
              </div>
              <button
                type="button"
                onClick={() => toggleActive(c)}
                className={c.is_active ? "text-emerald-600" : "text-slate-400"}
              >
                {c.is_active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
              </button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-[10px] uppercase text-slate-400">Bonus</p>
                <p className="font-semibold">{c.bonus_percent}% {c.bonus_flat ? `+${c.bonus_flat}` : ""}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-400">Granted</p>
                <p className="font-semibold">
                  {c.granted_count} / {(c.eligibility?.first_n_orgs) || "∞"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-400">Expiry</p>
                <p className="font-semibold">{c.bonus_expiry_days}d</p>
              </div>
            </div>
            {c.ribbon_text && (
              <p className="mt-3 text-[11px] italic text-slate-600 border-t border-slate-100 pt-2">
                Ribbon: "{c.ribbon_text}"
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================================
// Packs tab
// ============================================================================
function PacksPanel() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/admin/diploks/packs")
      .then((r) => setPacks(r.data.packs || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/diploks/packs/${p.tier_key}`, { is_active: !p.is_active });
      toast.success(`${p.label} ${p.is_active ? "deactivated" : "activated"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading packs…</p>;
  const currencies = ["INR", "USD", "AED", "EUR", "GBP", "HKD"];

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="diploks-admin-packs">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Pack</th>
            <th className="px-4 py-2 text-right">Diploks</th>
            <th className="px-4 py-2 text-right">Bonus</th>
            {currencies.map((c) => (
              <th key={c} className="px-3 py-2 text-right">{currencySymbol(c)} {c}</th>
            ))}
            <th className="px-3 py-2 text-center">Active</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {packs.map((p) => (
            <tr key={p.tier_key} data-testid={`diploks-admin-pack-${p.tier_key}`}>
              <td className="px-4 py-3">
                <p className="font-semibold text-[#0A192F]">{p.label}</p>
                <p className="text-[10px] text-slate-400 font-mono">{p.tier_key}</p>
              </td>
              <td className="px-4 py-3 text-right font-bold">{p.diploks?.toLocaleString()}</td>
              <td className="px-4 py-3 text-right text-emerald-700 font-semibold">+{p.bonus || 0}</td>
              {currencies.map((c) => (
                <td key={c} className="px-3 py-3 text-right text-[#0A192F]">
                  {p.prices?.[c] ? formatMoney(p.prices[c], c) : "—"}
                </td>
              ))}
              <td className="px-3 py-3 text-center">
                <button
                  type="button"
                  onClick={() => toggleActive(p)}
                  className={p.is_active ? "text-emerald-600" : "text-slate-400"}
                  data-testid={`diploks-admin-pack-toggle-${p.tier_key}`}
                >
                  {p.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ============================================================================
// Organisations tab — wallet viewer + manual adjust
// ============================================================================
function OrgsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowOnly, setLowOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [adjustOrg, setAdjustOrg] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.get(`/admin/diploks/organisations?low_balance_only=${lowOnly}&limit=200`)
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [lowOnly]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.organisation?.name || "").toLowerCase().includes(q) ||
      (r.wallet?.organisation_id || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="space-y-3" data-testid="diploks-admin-orgs">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search organisation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
            data-testid="diploks-admin-low-only"
          />
          Low-balance only
        </label>
      </div>
      {loading && <p className="text-sm text-slate-500">Loading wallets…</p>}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Organisation</th>
              <th className="px-4 py-2 text-right">Available</th>
              <th className="px-4 py-2 text-right">Trial</th>
              <th className="px-4 py-2 text-right">Bonus</th>
              <th className="px-4 py-2 text-right">Purchased</th>
              <th className="px-4 py-2 text-right">Used</th>
              <th className="px-4 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <tr key={r.wallet.organisation_id} data-testid={`diploks-admin-wallet-${r.wallet.organisation_id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#0A192F]">{r.organisation?.name || r.wallet.organisation_id}</p>
                  <p className="text-[10px] text-slate-400 font-mono">{r.wallet.organisation_id}</p>
                </td>
                <td className="px-4 py-3 text-right font-bold">{r.balance.total_available}</td>
                <td className="px-4 py-3 text-right">{r.balance.breakdown.trial.available}</td>
                <td className="px-4 py-3 text-right">{r.balance.breakdown.bonus.available}</td>
                <td className="px-4 py-3 text-right">{r.balance.breakdown.purchased.available}</td>
                <td className="px-4 py-3 text-right text-slate-500">{r.balance.total_used}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => setAdjustOrg(r)}
                    data-testid={`diploks-admin-adjust-${r.wallet.organisation_id}`}
                    className="text-[10px] font-semibold text-[#0F4C81] hover:underline inline-flex items-center gap-1"
                  >
                    <Edit2 className="h-3 w-3" /> Adjust
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adjustOrg && (
        <AdjustModal
          org={adjustOrg}
          onClose={() => setAdjustOrg(null)}
          onSuccess={() => { setAdjustOrg(null); refresh(); }}
        />
      )}
    </div>
  );
}

function AdjustModal({ org, onClose, onSuccess }) {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [diploks_type, setType] = useState("purchased");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (Math.abs(delta) < 1 || reason.trim().length < 4) {
      toast.error("Provide a delta and a reason (min 4 chars)");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/diploks/adjust", {
        organisation_id: org.wallet.organisation_id,
        delta: Number(delta),
        reason,
        diploks_type,
      });
      toast.success("Adjustment recorded in ledger");
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="diploks-admin-adjust-modal">
      <div className="bg-white rounded-xl border border-slate-200 max-w-md w-full p-5 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-wider font-bold text-[#D4AF37]">Manual Diploks Adjustment</p>
          <p className="text-sm font-bold text-[#0A192F] mt-1">{org.organisation?.name || org.wallet.organisation_id}</p>
          <p className="text-[11px] text-slate-500">Current available: {org.balance.total_available} Diploks</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Delta (positive to credit, negative to debit)</label>
          <input
            type="number"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            data-testid="diploks-admin-adjust-delta"
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Diploks type</label>
          <select
            value={diploks_type}
            onChange={(e) => setType(e.target.value)}
            data-testid="diploks-admin-adjust-type"
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          >
            <option value="purchased">Purchased</option>
            <option value="bonus">Bonus</option>
            <option value="subscription">Subscription</option>
          </select>
          <p className="text-[10px] text-slate-500 mt-1">Trial Diploks cannot be adjusted manually.</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Reason (required, min 4 chars, audit-logged)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="diploks-admin-adjust-reason"
            rows={3}
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs font-semibold px-3 py-2 rounded-md border border-slate-200">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="diploks-admin-adjust-submit"
            className="text-xs font-semibold px-4 py-2 rounded-md bg-[#0A192F] text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Apply adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Ledger tab
// ============================================================================
function LedgerPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/diploks/ledger?limit=200")
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <p className="text-sm text-slate-500">Loading ledger…</p>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="diploks-admin-ledger">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">When</th>
            <th className="px-4 py-2 text-left">Org</th>
            <th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Action</th>
            <th className="px-4 py-2 text-left">Feature</th>
            <th className="px-4 py-2 text-right">Added</th>
            <th className="px-4 py-2 text-right">Used</th>
            <th className="px-4 py-2 text-right">Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.ledger_id}>
              <td className="px-4 py-2 text-slate-500">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
              <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{r.organisation_id}</td>
              <td className="px-4 py-2 capitalize">{r.diploks_type}</td>
              <td className="px-4 py-2 font-semibold">{r.action_type}</td>
              <td className="px-4 py-2 text-slate-500 font-mono text-[10px]">{r.feature_key || "—"}</td>
              <td className="px-4 py-2 text-right text-emerald-700 font-semibold">{r.amount_added || ""}</td>
              <td className="px-4 py-2 text-right text-slate-700 font-semibold">{r.amount_used || ""}</td>
              <td className="px-4 py-2 text-right font-bold">{r.balance_after}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
